import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { PrimaryLinks } from './PrimaryLinks';
import { createLogoHeights, Logo, LogoProps } from '../Logo';
import { Editions } from './Editions';
import { CtaButton, CtaDataProps } from './CtaButton';
import { Menu } from './menu/Menu';
import { primaryHeaderHeights, reducedPrimaryHeaderHeights } from '../header.utils';
import { MenuLinkDataProps } from './menu/links/MenuLink';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { HUGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { VideoLogo, InjectedVideoLogoApiProps } from './VideoLogo';
import { createStyleForBoxShadow } from '../../../../../theming/boxShadow';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { LinkButtonDataProps } from '../../utils';

const componentConfigThemeKey = 'primaryHeader';

interface MenuData {
  links: MenuLinkDataProps[];
  buttons?: Array<LinkButtonDataProps>;
}

interface Link {
  text: string;
  href: string;
}

export interface NavigationLink {
  link: Link;
  children: NavigationLink[] | null;
}

export interface PrimaryHeaderDataProps {
  links: NavigationLink[] | null;
  logo: LogoProps;
  editions: NavigationLink[] | null;
  cta: CtaDataProps | null;
  menu: MenuData | null;
  videoLogo: InjectedVideoLogoApiProps | null;
  pagePath: string;
  editionEndpoint: string;
  menuAnimation?: AnimationDirectionType;
  reduceHeightAnimationDuration?: number;
}

export interface PrimaryHeaderThemeProps {
  baseUnit: BaseUnit;
  backgroundColor: string;
  boxShadowTypeNormal: string;
}

const getStyle = (baseUnit: BaseUnit, backgroundColor: string, menuAnimation: AnimationDirectionType, reduceHeightAnimationDuration: number) => {
  return StyleSheet.create({
    headerWrapper: {
      position: 'relative',
      backgroundColor,
      zIndex: 100,
    },
    header: {
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${reducedPrimaryHeaderHeights.large}px` : `${primaryHeaderHeights.large}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${primaryHeaderHeights.medium}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${primaryHeaderHeights.small}px`,
      },
      display: 'flex',
      justifyContent: 'space-between',
      maxWidth: `${HUGE_SCREEN_SIZE}px`,
      margin: '0 auto',
      transition: `${reduceHeightAnimationDuration}ms`,
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      gridColumnGap: `${baseUnit}px`,
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingRight: `${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: `${baseUnit / 2}px`,
      },
    },
    itemsLeftContainer: {
      paddingLeft: `${baseUnit}px`,
    },
  });
};

export const PrimaryHeader: React.FunctionComponent<PrimaryHeaderDataProps> = props => {
  const { links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint, menuAnimation = AnimationDirection.NONE, reduceHeightAnimationDuration = 0 } = props;
  const { baseUnit, backgroundColor, boxShadowTypeNormal } = useTheme<PrimaryHeaderThemeProps>(componentConfigThemeKey);
  const style = getStyle(baseUnit, backgroundColor, menuAnimation, reduceHeightAnimationDuration);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });

  return (
    <ErrorBoundary>
      <div className={css(style.headerWrapper, boxShadowStyle.style)}>
        <div className={css(style.header)}>
          <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
            {menu ? <Menu links={menu.links} editions={editions} editionEndpoint={editionEndpoint} buttons={menu.buttons} /> : null}
            {videoLogo
              ? <VideoLogo {...videoLogo} href={logo.href} menuAnimation={menuAnimation} reduceHeightAnimationDuration={reduceHeightAnimationDuration} />
              : <Logo {...logo} logoHeights={createLogoHeights(30, 30, 40)} menuAnimation={menuAnimation} reduceHeightAnimationDuration={reduceHeightAnimationDuration} />}
            {links ? <PrimaryLinks links={links} pagePath={pagePath} /> : null}
          </div>
          <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
            {cta ? <CtaButton {...cta} /> : null}
            {editions ? <Editions editions={editions} editionEndpoint={editionEndpoint} /> : null}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
